/**
 * Role-based field permissions for the LEAD record (v1.1) — UI mirror of the
 * backend authority (backend/src/services/leadFieldPermissions.js). Drives
 * read-only / hidden rendering on LeadDetail; the backend rejects locked edits.
 *
 * Lead matrix: Owner / Status / Rating / BANT are Sales-owned (Sales+Admin edit,
 * others read); Marketing Status is Marketing/BDR/Admin edit, Standard read,
 * Sales none. Everything else edits for all roles.
 */
import type { UserType } from '@/types'

export type FieldPerm = 'edit' | 'read' | 'none'
export type LeadRole = 'Admin' | 'Marketing' | 'BDR' | 'Sales' | 'Standard'

export function leadRoleOf(userType?: UserType | string | null): LeadRole {
  if (userType === 'Super Admin' || userType === 'Admin') return 'Admin'
  if (userType === 'Marketing') return 'Marketing'
  if (userType === 'BDR') return 'BDR'
  if (userType === 'User') return 'Sales'
  return 'Standard'
}

const SALES_OWNED: Record<LeadRole, FieldPerm> = {
  Admin: 'edit',
  Sales: 'edit',
  Marketing: 'read',
  BDR: 'read',
  Standard: 'read',
}

const LEAD_FIELD_PERMS: Record<string, Record<LeadRole, FieldPerm>> = {
  owner_id: SALES_OWNED,
  status: SALES_OWNED,
  rating: SALES_OWNED,
  budget__c: SALES_OWNED,
  authority__c: SALES_OWNED,
  need__c: SALES_OWNED,
  timeline__c: SALES_OWNED,
  budget_status__c: SALES_OWNED,
  authority_status__c: SALES_OWNED,
  need_status__c: SALES_OWNED,
  timeline_status__c: SALES_OWNED,
  marketing_status__c: {
    Admin: 'edit',
    Marketing: 'edit',
    BDR: 'edit',
    Standard: 'read',
    Sales: 'none',
  },
}

export function leadFieldPerm(role: LeadRole, field: string): FieldPerm {
  return LEAD_FIELD_PERMS[field]?.[role] ?? 'edit'
}
